<template>
    <section v-editable="blok" class="pp-table-section airport-page-section">
        <div class="pp-table-section__container md:container">
            <h2 class="pp-table-section__title airport-page-title">
                {{ title }}
            </h2>
            <RichText :richtext="blok.description" class="pp-table-section__descr" />
            <div class="pp-table-section__table">
                <table class="pp-table">
                    <thead class="pp-table__head">
                        <tr>
                            <th>{{ blok.first_column_title }}</th>
                            <th>4 day parking*</th>
                            <th>8 day parking*</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(product, index) in products" :key="index" class="pp-table__row">
                            <td>
                                <NuxtLink :to="getL4InternalLink(product.productLink)" class="pp-table__link-mobile">
                                    {{ product.productName }}
                                </NuxtLink>
                                <span class="pp-table__text-desktop">
                                    {{ product.productName }}
                                </span>
                            </td>
                            <td>
                                <template v-if="product.fourDaysPrice">
                                    {{ blok.currency }}{{ Number(product.fourDaysPrice).toFixed(2) }}
                                </template>
                                <template v-else>-</template>
                            </td>
                            <td>
                                <template v-if="product.eightDaysPrice">
                                    {{ blok.currency }}{{ Number(product.eightDaysPrice).toFixed(2) }}
                                </template>
                                <template v-else>-</template>
                            </td>
                            <td>
                                <NuxtLink :to="getL4InternalLink(product.productLink)" class="pp-table__link-desktop">
                                    {{ t('view_more') }}
                                </NuxtLink>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="blok.table_description" class="pp-table-section__table-descr">
                    {{ blok.table_description }}
                </div>
            </div>
            <div v-if="blok.products?.length > 4" class="pp-table__btn">
                <ShowMoreBtn
                    :is-opened="isOpened"
                    :show-text="t('show_more_products')"
                    :hide-text="t('hide_products')"
                    @click="isOpened = !isOpened"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { IL4ProductPricingTable, IL4ProductPricingTableItem } from '~/types/components/L4ProductPricingTable';
import { getL4InternalLink } from '~/utils/helpers';

const { blok } = defineProps<{ blok: IL4ProductPricingTable }>();

const { t } = useI18n();

const title = computed(() => blok.title.replace('{min_price}', minimumPrice.value));

const { width } = useWindowSize();

const products = computed<IL4ProductPricingTableItem[]>(() => {
    let visibleProducts;
    const products = blok.products ?? [];
    if (width.value > 767) {
        visibleProducts = products;
    } else {
        visibleProducts = isOpened.value ? products : blok.products.slice(0, 4);
    }
    return visibleProducts;
});

const isOpened = ref<boolean>(false);

const minimumPrice = computed(() =>
    products.value.reduce((minPrice: any, product: IL4ProductPricingTableItem) => {
        const { fourDaysPrice, eightDaysPrice } = product;
        const pricesArray: number[] = [];
        if (fourDaysPrice) {
            pricesArray.push(fourDaysPrice / 4);
        }
        if (eightDaysPrice) {
            pricesArray.push(eightDaysPrice / 8);
        }
        return Math.min(...pricesArray, minPrice)?.toFixed(2);
    }, 100000)
);
</script>

<style lang="postcss" scoped>
.pp-table-section {
    &__descr {
        @apply mb-8 md:text-xl;
    }
    &__table-descr {
        @apply text-xs md:text-sm md:leading-7 text-[#343A40] mt-2;
    }
}

.pp-table {
    @apply w-full;
    &__head {
        border-bottom: 1px solid #8b8585;
    }
    &__row {
        &:nth-child(even) {
            background: #f4f4f5;
        }
    }
    td,
    th {
        @apply text-center px-2 py-1 leading-6;
        &:first-child {
            @apply text-left;
        }
    }
    td {
        &:last-child {
            @apply hidden md:block;
        }
    }
    &__text-desktop {
        @apply hidden md:block;
    }
    &__link-mobile {
        @apply block md:hidden underline;
    }
    &__link-desktop {
        @apply text-[#0094FF] text-base hover:underline hover:text-[#0084E3];
    }
    &__btn {
        @apply md:hidden text-center mt-6;
    }
}

:deep(.rich-text p) {
    @apply text-xl leading-[30px] mb-8 mt-0;
}
</style>
